(() => {
  'use strict';

  const API_KEY = '__LPLScienceCompactV2';
  if (window[API_KEY]?.initAll) {
    window[API_KEY].initAll();
    return;
  }

  const LIMIT = 3;
  const HUB_STATE_KEY = 'lpl-science-library-v2-state';
  const JOURNEY_KEY = 'lpl-science-journey-id';
  const indexRequests = new Map();

  const normalized = (value) => String(value || '')
    .normalize('NFKD')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  const wordsFor = (value) => normalized(value).split(' ').filter(Boolean);
  const wordPrefixMatch = (tokens, words) => tokens.every((token) => words.some((word) => word.startsWith(token)));
  // Index entries are immutable: normalize each guide once, not on every keystroke.
  const searchData = new WeakMap();
  const searchFields = (entry) => {
    let fields = searchData.get(entry);
    if (!fields) {
      const title = normalized(entry.t);
      fields = {
        title,
        titleWords: title.split(' ').filter(Boolean),
        haystackWords: wordsFor(`${entry.t} ${entry.d} ${entry.c} ${(entry.q || []).join(' ')}`)
      };
      searchData.set(entry, fields);
    }
    return fields;
  };
  const uuid = () => self.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  const queryBucket = (value) => {
    const length = String(value || '').trim().length;
    if (!length) return '0';
    if (length <= 3) return '1-3';
    if (length <= 10) return '4-10';
    if (length <= 24) return '11-24';
    return '25+';
  };
  const analyticsAllowed = () => {
    try {
      return Boolean(window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.());
    } catch {
      return false;
    }
  };
  const journeyId = () => {
    if (!analyticsAllowed()) return null;
    let id = sessionStorage.getItem(JOURNEY_KEY);
    if (!id) {
      id = uuid();
      sessionStorage.setItem(JOURNEY_KEY, id);
    }
    return id;
  };
  const popupCell = () => {
    try {
      return window.LPLPopupExperiment?.getAssignment?.()?.factorial_cell_id || 'unassigned';
    } catch {
      return 'unavailable';
    }
  };
  const track = (root, params) => {
    if (!analyticsAllowed() || typeof window.gtag !== 'function') return;
    window.gtag('event', 'lpl_science_journey', {
      schema_version: 2,
      event_id: uuid(),
      journey_id: journeyId(),
      science_action: params.science_action,
      surface: root.dataset.surface || 'supporting-module',
      module_id: root.dataset.moduleId || 'science-search-compact-v2',
      content_id: params.content_id || 'science-library',
      index_version: params.index_version || 'fallback',
      topic_id: params.topic_id || 'all',
      species: params.species || 'all',
      product_lane: 'mixed',
      destination_type: params.destination_type || 'none',
      rank: Number(params.rank || 0),
      results_count: Number(params.results_count || 0),
      query_len_bucket: params.query_len_bucket || '0',
      filter_count: Number(params.filter_count || 0),
      ux_experiment_id: 'none',
      ux_arm: 'compact-v2',
      popup_factorial_cell_id: popupCell(),
      index_status: params.index_status || 'fallback',
      send_to: 'G-K65264H0RW',
      transport_type: 'beacon'
    });
  };
  const sizedImageUrl = (value, width) => {
    try {
      const url = new URL(value, window.location.href);
      if (url.hostname.endsWith('cdn.shopify.com')) url.searchParams.set('width', String(width));
      return url.toString();
    } catch {
      return value;
    }
  };
  const destinationType = (url) => {
    if (/\/pages\/what-is-/.test(url)) return 'what-is';
    if (/\/pages\/.+(calculator|checker|quiz|finder)/.test(url)) return 'tool';
    if (/\/products\//.test(url)) return 'product';
    return 'article';
  };
  const loadIndex = (url) => {
    if (indexRequests.has(url)) return indexRequests.get(url);
    const request = fetch(url, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((index) => {
        if (index.schema_version !== 'lpl.science-search-index.1.0.0' || !Array.isArray(index.entries)) {
          throw new Error('Unsupported Science Library index');
        }
        return index;
      });
    indexRequests.set(url, request);
    return request;
  };

  const makeCard = (entry, images, rank) => {
    const item = document.createElement('li');
    const article = document.createElement('article');
    const link = document.createElement('a');
    const media = document.createElement('span');
    const body = document.createElement('span');
    const meta = document.createElement('span');
    const category = document.createElement('span');
    const title = document.createElement('h3');
    const summary = document.createElement('span');
    const action = document.createElement('span');

    article.className = 'lpl-slc-card';
    article.dataset.contentId = entry.id;
    article.dataset.rank = String(rank);
    link.className = 'lpl-slc-card__link';
    link.href = entry.u;
    link.dataset.slcCardLink = '';
    media.className = 'lpl-slc-card__media';
    media.setAttribute('aria-hidden', 'true');

    const imageUrl = typeof entry.i === 'number' ? images[entry.i] : entry.i;
    if (imageUrl) {
      const image = document.createElement('img');
      image.className = 'lpl-slc-card__image';
      image.src = sizedImageUrl(imageUrl, 640);
      image.srcset = [320, 480, 640, 760].map((width) => `${sizedImageUrl(imageUrl, width)} ${width}w`).join(', ');
      image.sizes = '(min-width: 990px) 30vw, 92vw';
      image.width = 760;
      image.height = 475;
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      image.addEventListener('error', () => {
        const placeholder = document.createElement('span');
        placeholder.className = 'lpl-slc-card__placeholder';
        media.replaceChildren(placeholder);
      }, { once: true });
      media.append(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'lpl-slc-card__placeholder';
      media.append(placeholder);
    }

    body.className = 'lpl-slc-card__body';
    meta.className = 'lpl-slc-card__meta';
    category.textContent = entry.c || 'Science guide';
    meta.append(category);
    if (entry.r) {
      const readTime = document.createElement('span');
      readTime.textContent = `${entry.r} min read`;
      meta.append(readTime);
    }
    title.className = 'lpl-slc-card__title';
    title.textContent = entry.t;
    summary.className = 'lpl-slc-card__summary';
    summary.textContent = entry.d || '';
    action.className = 'lpl-slc-card__action';
    action.textContent = 'Read guide →';
    body.append(meta, title, summary, action);
    link.append(media, body);
    article.append(link);
    item.append(article);
    return item;
  };

  const initialize = (root) => {
    if (root.dataset.slcInitialized === 'true') return;
    root.dataset.slcInitialized = 'true';

    const form = root.querySelector('[data-slc-form]');
    const query = root.querySelector('[data-slc-query]');
    const topic = root.querySelector('[data-slc-topic]');
    const species = root.querySelector('[data-slc-species]');
    const results = root.querySelector('[data-slc-results]');
    const status = root.querySelector('[data-slc-status]');
    const actions = root.querySelector('[data-slc-actions]');
    const clear = root.querySelector('[data-slc-clear]');
    const libraryLink = root.querySelector('[data-slc-library-link]');
    if (!form || !query || !topic || !species || !results || !status || !actions || !clear || !libraryLink) return;

    let index;
    let engaged = false;
    let debounceTimer;
    let lastSignature = '';
    const initialTopic = topic.value;
    const initialSpecies = species.value;

    const saveHubState = () => {
      try {
        sessionStorage.setItem(HUB_STATE_KEY, JSON.stringify({
          query: query.value,
          topic: topic.value,
          species: species.value
        }));
      } catch {}
    };
    const hide = () => {
      engaged = false;
      results.replaceChildren();
      results.hidden = true;
      results.setAttribute('aria-busy', 'false');
      actions.hidden = true;
      status.textContent = '';
    };
    const matches = () => {
      const phrase = normalized(query.value);
      const tokens = wordsFor(query.value);
      const currentPath = root.dataset.excludeCurrent === 'true' ? root.dataset.currentPath : '';
      const ranked = index.entries
        .filter((entry) => !currentPath || entry.u !== currentPath)
        .filter((entry) => !topic.value || entry.c === topic.value)
        .filter((entry) => !species.value || entry.s === species.value || entry.s === 'both')
        .map((entry) => {
          if (!tokens.length) return { entry, score: 0 };
          const { title, titleWords, haystackWords } = searchFields(entry);
          if (!wordPrefixMatch(tokens, haystackWords)) return null;
          let score = title.includes(phrase) ? 100 : 0;
          tokens.forEach((token) => {
            score += titleWords.some((word) => word.startsWith(token)) ? 12 : 2;
          });
          return { entry, score };
        })
        .filter(Boolean);
      if (tokens.length) ranked.sort((a, b) => b.score - a.score || a.entry.t.localeCompare(b.entry.t));
      return ranked.map(({ entry }) => entry);
    };
    const render = (action = 'search_rendered') => {
      if (!engaged || !index) return;
      const found = matches();
      const shown = found.slice(0, LIMIT);
      results.setAttribute('aria-busy', 'true');
      results.replaceChildren(...shown.map((entry, position) => makeCard(entry, index.images || [], position + 1)));
      results.setAttribute('aria-busy', 'false');
      results.hidden = shown.length === 0;
      actions.hidden = false;
      status.textContent = found.length
        ? `${found.length} science guide${found.length === 1 ? '' : 's'} found. Showing ${shown.length}.`
        : 'No matching guides. Try fewer words or browse the complete library.';
      const signature = `${normalized(query.value)}|${topic.value}|${species.value}|${found.length}`;
      if (signature !== lastSignature) {
        lastSignature = signature;
        track(root, {
          science_action: action,
          index_version: index.index_version,
          index_status: 'ready',
          topic_id: topic.value ? `lpl:topic:${normalized(topic.value).replaceAll(' ', '-')}` : 'all',
          species: species.value || 'all',
          results_count: found.length,
          query_len_bucket: queryBucket(query.value),
          filter_count: Number(Boolean(topic.value)) + Number(Boolean(species.value))
        });
      }
    };
    const handleFailure = () => {
      results.replaceChildren();
      results.hidden = true;
      results.setAttribute('aria-busy', 'false');
      actions.hidden = false;
      status.textContent = 'Search is temporarily unavailable. Browse the complete Science Library instead.';
    };
    const ensureIndex = () => {
      if (index) return Promise.resolve(index);
      status.textContent = 'Loading the Science Library…';
      return loadIndex(root.dataset.indexUrl)
        .then((loaded) => {
          index = loaded;
          return loaded;
        });
    };
    const run = (action = 'search_rendered') => {
      engaged = true;
      ensureIndex().then(() => render(action)).catch(handleFailure);
    };

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      run('search_rendered');
    });
    query.addEventListener('input', () => {
      engaged = true;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => run('search_rendered'), 250);
    });
    [topic, species].forEach((control) => control.addEventListener('change', () => run('filter_changed')));
    clear.addEventListener('click', () => {
      query.value = '';
      topic.value = initialTopic;
      species.value = initialSpecies;
      hide();
      query.focus();
    });
    libraryLink.addEventListener('click', () => {
      saveHubState();
      track(root, {
        science_action: 'bridge_clicked',
        index_version: index?.index_version || 'fallback',
        index_status: index ? 'ready' : 'fallback',
        species: species.value || 'all',
        query_len_bucket: queryBucket(query.value),
        filter_count: Number(Boolean(topic.value)) + Number(Boolean(species.value))
      });
    });
    root.addEventListener('click', (event) => {
      const link = event.target.closest('[data-slc-card-link]');
      if (!link) return;
      const card = link.closest('[data-content-id]');
      track(root, {
        science_action: 'card_clicked',
        content_id: card?.dataset.contentId || 'unknown',
        destination_type: destinationType(link.getAttribute('href') || ''),
        rank: card?.dataset.rank,
        index_version: index?.index_version || 'fallback',
        index_status: index ? 'ready' : 'fallback',
        species: species.value || 'all',
        query_len_bucket: queryBucket(query.value),
        filter_count: Number(Boolean(topic.value)) + Number(Boolean(species.value))
      });
    });

    hide();
  };

  const initAll = (scope = document) => scope
    .querySelectorAll?.('[data-lpl-science-compact-v2]')
    .forEach(initialize);

  window[API_KEY] = { initAll };
  initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
